import React, { useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { Shield, Lock, Mail, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { useAdminAuth } from './AdminAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

export function AdminLoginPage() {
  const { admin, loading, login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (admin) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.success) navigate('/admin');
    else setError(res.error || 'Login failed');
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background:
          'radial-gradient(circle at 20% 20%, #1e293b 0%, #0f172a 40%, #020617 100%)',
      }}
    >
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 mb-4">
            <Shield className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight">
            SuperAdmin Access
          </h1>
          <p className="text-slate-400 text-sm mt-2">
            Restricted area — authorized personnel only.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-2xl p-8 shadow-2xl"
        >
          {error && (
            <div
              data-testid="admin-login-error"
              className="mb-5 flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm"
            >
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <label className="block mb-4">
            <span className="text-xs font-medium text-slate-300 uppercase tracking-wider mb-1.5 block">
              Admin Email
            </span>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="pl-9 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-amber-500"
                placeholder="admin@example.com"
              />
            </div>
          </label>

          <label className="block mb-6">
            <span className="text-xs font-medium text-slate-300 uppercase tracking-wider mb-1.5 block">
              Password
            </span>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="pl-9 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-amber-500"
                placeholder="••••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                data-testid="admin-login-password-toggle"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-amber-400 hover:bg-slate-700/40 transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="mt-2 text-right">
              <Link
                to="/admin/forgot-password"
                data-testid="admin-login-forgot-password"
                className="text-xs text-amber-400 hover:text-amber-300 hover:underline"
              >
                Forgot password?
              </Link>
            </div>
          </label>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
          >
            {submitting ? 'Authenticating…' : 'Sign in to Admin Panel'}
          </Button>

          <p className="mt-5 text-xs text-slate-500 text-center">
            5 failed attempts will lock this account for 15 minutes.
          </p>
        </form>
      </div>
    </div>
  );
}
